import React, { useState, useEffect } from "react";
import "katex/dist/katex.min.css";
import katex from "katex";
import { Remarkable } from "remarkable";
import { useTranslation } from "react-i18next";
/* Icons */
import icon_alignLeft from "../../assets/icons/format_align_left.svg";
import icon_alignRight from "../../assets/icons/format_align_right.svg";
import icon_alignCenter from "../../assets/icons/format_align_center.svg";
import icon_alignJustify from "../../assets/icons/format_align_justify.svg";

const MentuEditor = ({ plan, onPlan }) => {
  const [htmlContent, setHtmlContent] = useState("");
  const [, setEditableContent] = useState("");
  const { i18n } = useTranslation();

  useEffect(() => {
    if (plan) {
      processText(plan);
    }
    // eslint-disable-next-line
  }, [plan]);

  const convertListMarkdown = (markdownText) => {
    let processedText = markdownText;

    // Busca y procesa bloques de listas con guiones

    processedText = processedText.replace(
      /^[\s]*\-\s+(.+)/gm, // eslint-disable-line
      (match, text) => `<li class="editor-li">${text.trim()}</li>`
    );

    // Elimina líneas vacías entre las etiquetas <ul> y <ol>
    processedText = processedText
      .replace(/<\/ul>\s+<ul>/g, "</ul><ul>")
      .replace(/<\/ol>\s+<ol>/g, "</ol><ol>");

    return processedText;
  };

  /*const processText = (textToProcess) => {
    const md = new Remarkable({
      html: true, // Permite HTML en el input
      breaks: true, // Convierte '\n' en <br>
      typographer: true, // Habilita algunas sustituciones tipográficas
      command: false
    });

    // KaTeX pre-processing here if needed
    const parsedText = convertListMarkdown(textToProcess);
    let html = md.render(parsedText);


    // KaTeX post-processing
    html = html.replace(/\$(.+?)\$/g, (match, formula) => {
      try {
        return katex.renderToString(formula, {
          throwOnError: false,
          displayMode: false,
        });
      } catch (error) {
        console.error("Error rendering formula with KaTeX:", error);
        return match;
      }
    });

    setHtmlContent(html);
  };*/

  const processText = (textToProcess) => {
    const md = new Remarkable({
      html: true, // Permite HTML en el input
      breaks: true, // Convierte '\n' en <br>
      typographer: true, // Habilita algunas sustituciones tipográficas
      command: false,
    });

    const rubricGuide_es = `
    
  ### Guía para usar la rúbrica
  
  Las rúbricas son herramientas valiosas en los procesos de enseñanza y aprendizaje, que permiten a los docentes evaluar el progreso de sus estudiantes, y a estos tener claridad de su desempeño. Las rúbricas permiten valorar de manera clara y equitativa el progreso de los y las estudiantes, lo que facilita identificar fortalezas y áreas de mejora.
  
  Para asegurar su aplicación efectiva, te sugiero seguir los siguientes pasos:
  
  **1. Preparación:** Antes de iniciar la evaluación, comprende los criterios y las descripciones de cada nivel de desempeño en la rúbrica para garantizar unas asignaciones justas.
  
  **2. Evaluación:** Observa cómo los estudiantes aplican sus conocimientos y habilidades en relación con los criterios establecidos. Toma notas descriptivas en tu planilla o cuaderno.
  
  **3. Registro:** Documenta el nivel de desempeño alcanzado por cada estudiante para cada criterio evaluado inmediatamente después de observar o leer su desempeño. Puedes marcar una *X* en la casilla correspondiente de la rúbrica para indicar el nivel de desempeño alcanzado.
  
  **4. Comentarios:** Si aplica, añade observaciones adicionales para ofrecer mayor claridad y retroalimentación sobre el rendimiento individual de tus estudiantes. Valora el esfuerzo y busca cultivar una mentalidad de crecimiento.
  
  **5. Metacognición:** Dedica tiempo en la clase a interpretar los resultados con los estudiantes, enseñándoles a leer la rúbrica y a planear cómo mejorar a partir de la retroalimentación.
  
  **6. Reflexión:** Al finalizar la evaluación, reflexiona sobre la efectividad de la rúbrica, considera ajustes para mejorar en futuras evaluaciones e identifica oportunidades de mejora para enriquecer la enseñanza y el aprendizaje.`;

  const rubricGuide_en = `
    
### Guide to Using the Rubric

Rubrics are valuable tools in teaching and learning processes, allowing teachers to assess their students' progress, and students to have clarity about their performance. Rubrics enable the clear and equitable assessment of students' progress, facilitating the identification of strengths and areas for improvement.

To ensure their effective application, I suggest following these steps:

**1. Preparation:** Before starting the evaluation, understand the criteria and descriptions of each performance level in the rubric to ensure fair assignments.

**2. Evaluation:** Observe how students apply their knowledge and skills in relation to the established criteria. Take descriptive notes in your spreadsheet or notebook.

**3. Recording:** Document the performance level achieved by each student for each evaluated criterion immediately after observing or reading their performance. You can mark an *X* in the corresponding box of the rubric to indicate the achieved performance level.

**4. Comments:** If applicable, add additional observations to provide greater clarity and feedback on the individual performance of your students. Value the effort and seek to cultivate a growth mindset.

**5. Metacognition:** Spend time in class interpreting the results with the students, teaching them to read the rubric and plan how to improve from the feedback.

**6. Reflection:** At the end of the evaluation, reflect on the effectiveness of the rubric, consider adjustments to improve in future evaluations, and identify opportunities for improvement to enrich teaching and learning.`;

    if (textToProcess.includes("Rúbrica") || textToProcess.includes("Rubric")) {
      textToProcess += i18n.language === "es" ? rubricGuide_es : rubricGuide_en; // Añade rubricGuide al final de html
    }

    let html;

    // KaTeX pre-processing here if needed
    const parsedText = convertListMarkdown(textToProcess);
    html = md.render(parsedText);

    // Detectar y extraer el fragmento JSON
    html = textToProcess.replace(
      /```json\n([\s\S]*?)\n```/g,
      (match, jsonString) => {
        try {
          // Convertir el JSON en una tabla HTML
          const jsonObj = JSON.parse(jsonString);
          return jsonToHtmlTable(jsonObj);
        } catch (error) {
          console.error("Error parsing JSON:", error);
          return match;
        }
      }
    );

    // KaTeX post-processing
    html = html.replace(/\$(.+?)\$/g, (match, formula) => {
      try {
        return katex.renderToString(formula, {
          throwOnError: false,
          displayMode: false,
        });
      } catch (error) {
        console.error("Error rendering formula with KaTeX:", error);
        return match;
      }
    });

    // Renderizar el Markdown a HTML (sin el fragmento JSON ya procesado)
    html = md.render(html);

    setHtmlContent(html);
  };

  // Función para convertir un objeto JSON a una tabla HTML
    function jsonToHtmlTable(jsonObj) {
    let tableHtml = '<table class="rubrica"><thead><tr>';

    // Revisar si jsonObj es un arreglo para manejar el formato de lista de objetos
    if (Array.isArray(jsonObj) && jsonObj[0] && 'Nivel de desempeño' in jsonObj[0] && 'Descripción' in jsonObj[0] && 'Ejemplo de respuesta' in jsonObj[0]) {
        // Encabezados para el formato de arreglo de objetos
        tableHtml += '<th>Nivel de desempeño</th><th>Descripción</th><th>Ejemplo de respuesta</th>';

        // Cerrar encabezado y abrir cuerpo de la tabla
        tableHtml += '</tr></thead><tbody>';

        // Iterar sobre el array para construir las filas de la tabla
        jsonObj.forEach(item => {
            tableHtml += `<tr><td>${item['Nivel de desempeño']}</td><td>${item['Descripción']}</td><td>${item['Ejemplo de respuesta']}</td></tr>`;
        });
    } else if (typeof jsonObj === 'object') {
        // Procesamiento para la estructura de objeto
        // Encuentra las claves de criterios y niveles
        const criteriosKey = Object.keys(jsonObj).find(key => key.toLowerCase().includes('criterio'));
        const nivelesKey = Object.keys(jsonObj).find(key => key.toLowerCase().includes('nivel') && key.toLowerCase().includes('desempeño'));

        if (!criteriosKey || !nivelesKey) {
           throw new Error("Clave de criterios o niveles de desempeño no encontrada");
        }

        // Construir los encabezados basados en los criterios y niveles de desempeño
        tableHtml += "<th>Criterios</th>";

        Object.keys(jsonObj[nivelesKey]).forEach(nivel => {
           tableHtml += `<th class="niveles">${nivel}</th>`;
        });

        // Verificar y agregar columnas de Comentarios si existen
        if (jsonObj.hasOwnProperty('Comentarios')) {
           tableHtml += "<th>Comentarios</th>";
        }

        tableHtml += '</tr></thead><tbody>';

        // Construir las filas de criterios
        Object.keys(jsonObj[criteriosKey]).forEach(criterio => {
            tableHtml += `<tr><td>${jsonObj[criteriosKey][criterio]}</td>`;

            Object.keys(jsonObj[nivelesKey]).forEach(() => {
                tableHtml += "<td></td>";
            });

            if (jsonObj.hasOwnProperty('Comentarios')) {
                tableHtml += "<td></td>";
            }

            tableHtml += "</tr>";
        });
    } else {
        // Error para formatos no reconocidos
        throw new Error("Formato de JSON no reconocido");
    }

    // Cerrar cuerpo y tabla
    tableHtml += "</tbody></table>";
    return tableHtml;
  }

  const applyStyle = (command) => {
    document.execCommand(command, false, null);
  };

  const applyUnderline = () => {
    document.execCommand("underline", false, "");
  };

  const applyStrikethrough = () => {
    document.execCommand("strikeThrough", false, "");
  };

  const alignLeft = () => {
    document.execCommand("justifyLeft", false, "");
  };

  const alignRight = () => {
    document.execCommand("justifyRight", false, "");
  };

  const alignCenter = () => {
    document.execCommand("justifyCenter", false, "");
  };

  const alignJustify = () => {
    document.execCommand("justifyFull", false, "");
  };

  const handleContentEditable = (event) => {
    const updatedHtml = event.target.innerHTML;
    setEditableContent(updatedHtml);
  };

  return (
    <div className="lg:max-h-includeBack sm-max:h-full flex w-full flex-col">
      <div className="flex w-full flex-row items-center justify-start gap-4 bg-white px-4 py-2 hover:bg-white">
        <button
          className=" flex h-8 w-8 items-center justify-center rounded font-bold text-black"
          onClick={() => applyStyle("bold")}
        >
          𝗕
        </button>
        <button
          className=" flex h-8 w-8 items-center justify-center rounded font-bold italic text-black"
          onClick={() => applyStyle("italic")}
        >
          I
        </button>
        <button
          className="flex h-8 w-8 items-center justify-center rounded font-bold text-black underline"
          onClick={applyUnderline}
        >
          U
        </button>
        <button
          className="flex h-8 w-8 items-center justify-center rounded font-bold text-black line-through"
          onClick={applyStrikethrough}
        >
          𝚂
        </button>
        <button
          className="h- flex w-8 items-center justify-center rounded font-bold text-black"
          onClick={alignLeft}
        >
          <img className="w-5 max-w-full" src={icon_alignLeft} alt="" />
        </button>
        <button
          className="h- flex w-8 items-center justify-center rounded font-bold text-black"
          onClick={alignCenter}
        >
          <img className="w-5 max-w-full" src={icon_alignCenter} alt="" />
        </button>
        <button
          className="flex h-8 w-8 items-center justify-center rounded font-bold text-black"
          onClick={alignRight}
        >
          <img className="w-5 max-w-full" src={icon_alignRight} alt="" />
        </button>
        <button
          className="h- flex w-8 items-center justify-center rounded font-bold text-black"
          onClick={alignJustify}
        >
          <img className="w-5 max-w-full" src={icon_alignJustify} alt="" />
        </button>
      </div>
      <div className="lg-max:max-h-includeBack lg:max-h-includeBack sm-max:h-includeToggle flex justify-center overflow-auto pb-20 pl-0 pr-1 pt-0">
        <div
          className="mentu-editor preview h-full w-full"
          contentEditable
          dangerouslySetInnerHTML={{ __html: htmlContent }}
          onInput={handleContentEditable}
        />
      </div>
    </div>
  );
};

export default MentuEditor;
